import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { CrmUpboardPerson } from './interfaces/crm-upboard-person.interface';
import { UpboardStatusTypes } from 'src/app/constants/upboard-status-types';
import { ChangeUpboardStatus } from './interfaces/change-upboard-status.interface';
import { Observable, ReplaySubject, Subject, forkJoin} from 'rxjs';
import { SalesOpportunity } from './interfaces/sales-opportunity.interface';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/widgets/confirmation/confirmation-dialog/confirmation-dialog.component';
import { Router } from '@angular/router';
import { SalesOpportunityService } from 'src/app/services/sales-opportunity.service';
@Injectable({
  providedIn: 'root'
})
export class CrmUpboardService {

  // endpoints
  crmUpboardPersonEp: string = `${this._apiSvc.endpoints.crm}/api/CrmUpboardPerson`;
  upboardSubject = new ReplaySubject<{AvailablePersons: CrmUpboardPerson[], UnavailablePersons: CrmUpboardPerson[], SalesOpportunities: SalesOpportunity[] }>(1);
  upNextSubject = new Subject<boolean>();
  upboardIntervalId:  any;
  upNextIntervalId: any;

  //current user up next notified
  salesPersonId: number;
  storeRefId: number;
  upboardPersonId: number;

  //subscriptions
  getUpNext$: Observable<any>;

  pauseUpNextFetch: boolean = false;
  pauseFetch: boolean = false;

  constructor(
    private _apiSvc: ApiService,
    private _dialog: MatDialog,
    private _salesOpportunitySvc: SalesOpportunityService,
    public router: Router
  ) { }

  getCanBecomeActive(upboardPersonId: number): Observable<any> {
    return this._apiSvc.get(`${this.crmUpboardPersonEp}/CanBecomeActive/${upboardPersonId}`);
  }

  /**
 * Search upboard persons
 * @param params 
 * @returns 
 */
  search(params: any): any {
    return this._apiSvc.search(this.crmUpboardPersonEp, params)
      .pipe(
        map((res: any) => {
          return res.Data;
        })
      );
  }

  //set sales person id
  setSalesPersonId(salesPersonId: number): void {
    this.salesPersonId = salesPersonId;
  }

  //set upboard person id
  setUpboardPersonId(upboardPersonId: number): void {
    this.upboardPersonId = upboardPersonId;
  }

  /**
   * Get upboard person
   * @param id 
   * @returns 
   */
  get(id: number): any {
    return this._apiSvc.get(`${this.crmUpboardPersonEp}/${id}`);
  }

  getUpboardPersonBySalesPersonIdAndStore(salesPersonId: number, storeRefId: number): any {
    return this._apiSvc.get(`${this.crmUpboardPersonEp}/GetBySalesPersonId/${salesPersonId}/store/${storeRefId}`);
  }

  /**
   * Add upboard person
   * @param params 
   * @returns 
   */
  add(params: CrmUpboardPerson): any {
    return this._apiSvc.add(this.crmUpboardPersonEp, params);
  }

  /**
   * Update upboard person
   * @param params 
   * @returns 
   */
  update(params: CrmUpboardPerson): any {
    return this._apiSvc.update(`${this.crmUpboardPersonEp}/${params.Id}`, params);
  }

  /**
   * Delete upboard person
   * @param id 
   * @returns 
   */
  delete(id: number): any {
    return this._apiSvc.delete(`${this.crmUpboardPersonEp}/${id}`);
  }


  /**
   * Check In upboard person
   * 
   * 
   */
  checkIn(checkInParams): any {
    let currentDateAndTimeInLocal = new Date().toLocaleDateString() + ' ' + new Date().toLocaleTimeString();
    let checkInParameters: ChangeUpboardStatus = {
      SalesPersonId: checkInParams.SalesPersonId,
      UpboardStatusType: UpboardStatusTypes.Available,
      StatusChangeDtm: currentDateAndTimeInLocal,
      StoreRefId: checkInParams.StoreRefId
    };
    return this._apiSvc.update(`${this.crmUpboardPersonEp}/${checkInParams.SalesPersonId}/ChangeStatus`, checkInParameters);
  }

  /**
   * Check Out upboard person
   * 
   * 
   */
  checkOut(checkOutParams): any {
    let checkOutParameters: ChangeUpboardStatus = {
      SalesPersonId: checkOutParams.SalesPersonId,
      UpboardStatusType: UpboardStatusTypes.CheckedOut,
      StoreRefId: checkOutParams.StoreRefId
    };
    return this._apiSvc.update(`${this.crmUpboardPersonEp}/${checkOutParams.SalesPersonId}/ChangeStatus`, checkOutParameters);
  }

  /**
   * Change upboard person status
   * @param params 
   * @returns 
   */
  changeStatus(params: ChangeUpboardStatus): any {
    return this._apiSvc.update(`${this.crmUpboardPersonEp}/${params.SalesPersonId}/ChangeStatus`, params);
  }

  /**
   * Reorder upboard person list  
   *
   */
  reorder(newOrder: number[]): any {
    return this._apiSvc.update(`${this.crmUpboardPersonEp}/${this.storeRefId}/ReorderAvailable`, newOrder);
  }

  // fetch both unavailable and available upboard list every 4 seconds using forkjoin
  startFetchingUpboardList(): void {
    this.stopFetchingUpboardList();

    const getAvailable$ = this.search({ Filter: `UpboardStatus~eq~${UpboardStatusTypes.Available}~and~StoreRefId~eq~${this.storeRefId}`, sort: 'AvailableQueueSeq-asc' });
    const getUnavailable$ = this.search({ Filter: `UpboardStatus~eq~${UpboardStatusTypes.Unavailable}~and~StoreRefId~eq~${this.storeRefId}`});
    
    // get available status upboard records
    let searchParams = {
      Filter: `StoreRefId~eq~${this.storeRefId}~and~IsCompleted~eq~false~and~UpboardPersonId~neq~null`,
      sort: 'ActiveDtm-desc'
    };
    const getActiveSalesOpportunities$ = this._salesOpportunitySvc.search(searchParams);

    this.upboardIntervalId = setInterval(() => {
      if (!this.pauseFetch) {
        this.pauseFetch = true;
        forkJoin([getAvailable$, getUnavailable$, getActiveSalesOpportunities$]
        ).subscribe(([available, unavailable, active]: [CrmUpboardPerson[], CrmUpboardPerson[], SalesOpportunity[]]) => {
          this.pauseFetch = false;
          this.upboardSubject.next({AvailablePersons: available, UnavailablePersons: unavailable, SalesOpportunities: active});
        }, () => {
          this.pauseFetch = false;
        });}
    }, 4000);
  }

  startFetchingUpNext(): void {
    this.upNextSubject = null;
    this.stopFetchingUpNext();
    this.upNextIntervalId = null;

    this.getUpNext$ = this.getCanBecomeActive(this.upboardPersonId);

    this.upNextIntervalId = setInterval(() => {
      if (!this.pauseUpNextFetch) {
        this.pauseUpNextFetch = true;
        this.getUpNext$.subscribe((res: any) => {
          this.pauseUpNextFetch = false;
          this.upNextSubject = new Subject<boolean>();
          this.upNextSubject.next(res.Data);
          if (res.CanBecomeActive) {
            this.openUpNextDialog();
          }
        }, () => {
          this.pauseUpNextFetch = false;
        });
      }
    }, 4000);
  }

  getAllLists(): Observable<any> {
    const getAvailable$ = this.search({ Filter: `UpboardStatus~eq~${UpboardStatusTypes.Available}~and~StoreRefId~eq~${this.storeRefId}`, sort: 'AvailableQueueSeq-asc' });
    const getUnavailable$ = this.search({ Filter: `UpboardStatus~eq~${UpboardStatusTypes.Unavailable}~and~StoreRefId~eq~${this.storeRefId}`});
    
    // get available status upboard records
    let searchParams = {
      Filter: `StoreRefId~eq~${this.storeRefId}~and~IsCompleted~eq~false~and~UpboardPersonId~neq~null`,
      sort: 'ActiveDtm-desc'
    };
    const getActiveSalesOpportunities$ = this._salesOpportunitySvc.search(searchParams);
    return forkJoin([getAvailable$, getUnavailable$, getActiveSalesOpportunities$]);
  }

  //Stop Fetching up next
  stopFetchingUpNext(): void {
    clearInterval(this.upNextIntervalId);
  }

  stopFetchingUpboardList(): void {
    clearInterval(this.upboardIntervalId);
  }

  isCurrentUser(record: CrmUpboardPerson | SalesOpportunity): boolean {
    if (record.hasOwnProperty('SalesPersonId')) {
      return record.SalesPersonId === this.salesPersonId;
    }
  }

  isCurrentRoute(path: string): boolean {
    return this.router.url === path;
  }

  openUpNextDialog(): void {      // open confirmation dialog
    const dialogRef = this._dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      data: {
        title: 'You are up next!',
        message: '',
        confirmBtnLabel: 'Go To Upboard',
        cancelBtnLabel: 'Ok'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      let isCurrentRoute = this.isCurrentRoute('/crm/upboard');
      if (result) {
        this.router.navigate(['/crm/upboard']).then(() => {
          if (isCurrentRoute) {
            window.location.reload();
          }
          window.location.reload();
        });
      }
    });
  }
}
